import trackEvents from '../../../../../constants/track-events'
import { useDiscover } from '../../../../../features/discover/hooks/useDiscover'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES
} from '../../../../../features/discover/utils'
import { trackEvent } from '../../../../../helpers/analytics'
import { Document, Page, View } from '../../compiler'

import { colors, typoStyles } from '../../constants'
import { isUndefinedOrNull } from '../../utils'
import {
  getDimensionExplanation,
  hasRankedDimensions,
  isCultureLong
} from '../culture/culture-section'
import CultureRankedList from '../culture/CultureRankedList'
import DimensionCard from '../DimensionCard'
import CoordinateSystem from '../graphs/CoordinateSystem'
import MarkdownText from '../MarkdownText'
import DimensionCardAppendix from '../talentProfile/DimensionCard'
import TitleDescription from '../talentProfile/TitleDescription'
import CheckIcon from './CheckIcon'
import RequirementProfileLoading from './loading'
import MandatoryCriterion from './MandatoryCriterion'
import MessageIcon from './MessageIcon'
import SummaryIcon from './SummaryIcon'
import TopInterests from './TopInterests'

const RequirementProfile = ({
  data,
  withoutAttachments,
  hasCompletedOnboarding,
  isLoading
}) => {
  const {
    enableAssistant,
    setAssistantIsTyping,
    sendMessageToAssistant,
    setAssistantFocus
  } = useDiscover()
  // const { tGeneral } = useTranslationContext()

  // Show loading state
  if (isLoading) {
    return <RequirementProfileLoading />
  }

  // Guard against undefined data
  if (!data) {
    return null
  }

  const {
    // score_visible,
    attachment_visible,
    culture_visible,
    interests_visible
  } = data || {}

  const hasSkillsDimensions = data?.skills_section?.data?.length > 0
  const hasPersonalityDimensions = data?.personality_section?.data?.length > 0

  /* Den mode legt die Stelle fest (cultural_fit.mode, sonst der Partner). Im
     long mode gibt es weder talent_scores noch data, dafür ranked_dimensions
     und top_dimensions. */
  const isCultureLongMode = isCultureLong(data?.culture_section)

  const hasShortCulture =
    !isUndefinedOrNull(data?.culture_section?.talent_scores?.score_coopcomp) &&
    !isUndefinedOrNull(data?.culture_section?.talent_scores?.score_orderchaos)

  const hasCulture =
    (isCultureLongMode
      ? hasRankedDimensions(data?.culture_section)
      : hasShortCulture) && culture_visible

  const hasInterestsDimensions =
    data?.interests_section?.data?.length > 0 && interests_visible

  // const { HEROS_JOURNEY } = data.system.challenges.data.HEROS_JOURNEY;

  const pageProps = {
    // size: getPageSize(PDF_DPI_NUMBER),
    // wrap: true,
    style: { paddingTop: 64 }
  }

  const sendDescriptionToAssistant = (description) => {
    trackEvent(trackEvents.DISCOVER_CLICK_REQUIREMENT_PROFILE_DIMENSION_TITLE)
    enableAssistant()
    setAssistantIsTyping(true)
    setTimeout(() => {
      setAssistantIsTyping(false)
      sendMessageToAssistant({
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        description
      })
      setAssistantFocus(false)
    }, 1000)
  }

  const sendCultureDimensionToAssistant = (dimension) => {
    if (!hasCompletedOnboarding) return
    sendDescriptionToAssistant(getDimensionExplanation(dimension))
  }

  return (
    <Document>
      {/** HEADER + SUMMARY */}
      <Page>
        {/** AUF EINEN BLICK */}
        <View>
          <TitleDescription
            title={data?.overview_section?.title}
            description={data?.overview_section?.description}
          />
          <View
            id='requirement_profile_summary_section'
            style={{ marginHorizontal: 24, marginTop: 26 }}
          >
            <View
              style={{
                borderBottomWidth: 4,
                borderBottomColor: '#DFE4FC',
                backgroundColor: '#F5F7FA',
                paddingHorizontal: 24,
                paddingVertical: 16,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                borderLeftWidth: 1,
                borderTopWidth: 1,
                borderRightWidth: 1,
                borderLeftColor: '#EBF0F5',
                borderTopColor: '#EBF0F5',
                borderRightColor: '#EBF0F5',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <SummaryIcon style={{ marginRight: 16 }} />
              <MarkdownText style={typoStyles.H4Black}>
                {data?.overview_section?.container_title}
              </MarkdownText>
            </View>
            <View
              style={{
                backgroundColor: '#FAFBFC',
                borderLeftColor: '#EBF0F5',
                borderBottomColor: '#EBF0F5',
                borderRightColor: '#EBF0F5',
                borderLeftWidth: 1,
                borderBottomWidth: 1,
                borderRightWidth: 1,
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4
              }}
            >
              {data?.overview_section?.must_criteria?.data?.length > 0 && (
                <View
                  style={{
                    padding: 24,
                    borderBottomWidth: 1,
                    borderBottomColor: '#EBF0F5'
                  }}
                >
                  <MarkdownText
                    style={[
                      typoStyles.T2MediumBlue,
                      { textTransform: 'uppercase' }
                    ]}
                  >
                    {data?.overview_section?.must_criteria?.title}
                  </MarkdownText>
                  {(data?.overview_section?.must_criteria?.data || []).map(
                    (d, index) => (
                      <MandatoryCriterion
                        key={index}
                        style={{ marginTop: 16 }}
                        text={d}
                      />
                    )
                  )}
                </View>
              )}
              <View
                style={{
                  padding: 24,
                  borderBottomWidth: 1,
                  borderBottomColor: '#EBF0F5'
                }}
              >
                <MarkdownText
                  style={[
                    typoStyles.T2MediumBlue,
                    { textTransform: 'uppercase' }
                  ]}
                >
                  {data?.overview_section?.advantageous?.title}
                </MarkdownText>
                {(data?.overview_section?.advantageous?.data || []).map(
                  (d, index) => (
                    <View
                      key={index}
                      style={{
                        marginTop: 16,
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                    >
                      <CheckIcon />
                      <MarkdownText
                        style={[typoStyles.T1Black, { marginLeft: 18 }]}
                      >
                        {d}
                      </MarkdownText>
                    </View>
                  )
                )}
              </View>
              <View
                style={{
                  padding: 24
                }}
              >
                <MarkdownText
                  style={[
                    typoStyles.T2MediumBlue,
                    { textTransform: 'uppercase' }
                  ]}
                >
                  {data?.overview_section?.interview_recommendations?.title}
                </MarkdownText>
                {(
                  data?.overview_section?.interview_recommendations?.data || []
                ).map((d, index) => (
                  <View
                    key={index}
                    style={{
                      marginTop: 16,
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}
                  >
                    <MessageIcon />
                    <MarkdownText
                      style={[typoStyles.T1Black, { marginLeft: 18 }]}
                    >
                      {d}
                    </MarkdownText>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>
      </Page>
      {/** SKILLS */}
      {hasSkillsDimensions && (
        <Page id='requirement_profile_skills_section' {...pageProps}>
          <TitleDescription
            title={data?.skills_section?.title}
            description={data?.skills_section?.description}
          />
          <View style={{ marginTop: 10 }} />
          {(data?.skills_section?.data || []).map((d, index) => {
            const {
              inputValues,
              min_yellow,
              max_yellow,
              min_green,
              max_green,
              description
            } = d || {}
            return (
              <View
                key={index}
                style={{ marginTop: 16 }}
                id={`requirement_profile_skills_section_${index}`}
              >
                <DimensionCard
                  joyrideTargetId={`requirement_profile_skills_section_${index}_scala`}
                  style={{ paddingHorizontal: 24 }}
                  title={d?.title}
                  endpointLeft={d?.endpoint_left}
                  endpointRight={d?.endpoint_right}
                  beamGraphData={{
                    expertInputValues: inputValues,
                    minYellow: min_yellow,
                    maxYellow: max_yellow,
                    minGreen: min_green,
                    maxGreen: max_green
                  }}
                  description={d?.insight}
                  language={data?.language}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                  onClickDimensionTitle={() => {
                    if (!hasCompletedOnboarding) return
                    sendDescriptionToAssistant(description)
                  }}
                  dimensionTitleJoyrideId={`requirement_profile_skills_section_${index}_title`}
                />
              </View>
            )
          })}
        </Page>
      )}
      {/** PERSONALITY */}
      {hasPersonalityDimensions && (
        <Page id='requirement_profile_personality_section' {...pageProps}>
          <TitleDescription
            title={data?.personality_section?.title}
            description={data?.personality_section?.description}
          />
          <View style={{ marginTop: 10 }} />
          {(data?.personality_section?.data || []).map((d, index) => {
            const {
              inputValues,
              min_yellow,
              max_yellow,
              min_green,
              max_green,
              description
            } = d || {}

            return (
              <View
                id={`requirement_profile_personality_section_${index}`}
                key={index}
                style={{ marginTop: 16 }}
              >
                <DimensionCard
                  joyrideTargetId={`requirement_profile_personality_section_${index}_scala`}
                  style={{ paddingHorizontal: 24 }}
                  title={d?.title}
                  endpointLeft={d?.endpoint_left}
                  endpointRight={d?.endpoint_right}
                  beamGraphData={{
                    expertInputValues: inputValues,
                    minYellow: min_yellow,
                    maxYellow: max_yellow,
                    minGreen: min_green,
                    maxGreen: max_green
                  }}
                  description={d?.insight}
                  language={data?.language}
                  hasCompletedOnboarding={hasCompletedOnboarding}
                  onClickDimensionTitle={() => {
                    if (!hasCompletedOnboarding) return
                    sendDescriptionToAssistant(description)
                  }}
                />
              </View>
            )
          })}
        </Page>
      )}
      {/** CULTURE */}
      {hasCulture && (
        <Page {...pageProps}>
          <TitleDescription
            title={data?.culture_section?.title}
            description={data?.culture_section?.description}
          />
          {isCultureLongMode ? (
            <CultureRankedList
              id='requirement_profile_culture_section'
              containerStyle={{ marginHorizontal: 24, marginTop: 26 }}
              data={data?.culture_section?.ranked_dimensions}
              topDimensions={data?.culture_section?.top_dimensions}
              hasCompletedOnboarding={!!hasCompletedOnboarding}
              onClickDimension={sendCultureDimensionToAssistant}
            />
          ) : (
            <View
              id='requirement_profile_culture_section'
              style={{ marginHorizontal: 24, marginTop: 26 }}
            >
              <View
                style={{
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.lightGrey,
                  height: 72,
                  paddingHorizontal: 24,
                  alignItems: 'center'
                }}
              >
                <MarkdownText style={typoStyles.H4Black}>
                  {data?.culture_section?.score_title}
                </MarkdownText>
              </View>
              <View
                style={{
                  borderLeftWidth: 1,
                  borderRightWidth: 1,
                  borderColor: colors.lightGrey
                }}
              >
                <CoordinateSystem
                  companyCultureScores={{
                    HEROS_JOURNEY_score_coopcomp:
                      data?.culture_section?.talent_scores?.score_coopcomp,
                    HEROS_JOURNEY_score_orderchaos:
                      data?.culture_section?.talent_scores?.score_orderchaos
                  }}
                  withCompanyIcon
                />
              </View>

              <View
                style={{
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  backgroundColor: colors.lightPaleGrey,
                  borderWidth: 1,
                  borderColor: colors.lightGrey,
                  padding: 24
                }}
              >
                {data?.culture_section?.data?.map((c, index) => (
                  <MarkdownText
                    key={index}
                    style={[
                      typoStyles.T1Black,
                      { marginTop: index === 0 ? 0 : 16, display: 'block' }
                    ]}
                  >
                    {c}
                  </MarkdownText>
                ))}
              </View>
            </View>
          )}
        </Page>
      )}
      {/** INTERESSEN */}
      {hasInterestsDimensions && (
        <Page id='requirement_profile_interests_section' {...pageProps}>
          <TitleDescription
            title={data?.interests_section?.title}
            description={data?.interests_section?.description}
          />
          <TopInterests
            title={data?.interests_section?.sub_title}
            data={data?.interests_section?.data?.slice(0, 3)}
            containerStyle={{ marginHorizontal: 24, marginTop: 26 }}
            insight={data?.interests_section?.insight}
          />
          <DimensionCard
            style={{ paddingHorizontal: 24, marginTop: 16 }}
            title={data?.riasec_elevation_section?.data?.title}
            endpointLeft={data?.riasec_elevation_section?.endpole_left}
            endpointRight={data?.riasec_elevation_section?.endpole_right}
            beamGraphData={{
              expertInputValues:
                data?.riasec_elevation_section?.data?.inputValues,
              minYellow: data?.riasec_elevation_section?.data?.min_yellow,
              maxYellow: data?.riasec_elevation_section?.data?.max_yellow,
              minGreen: data?.riasec_elevation_section?.data?.min_green,
              maxGreen: data?.riasec_elevation_section?.data?.max_green
            }}
            description={data?.riasec_elevation_section?.insight}
            language={data?.language}
            hasCompletedOnboarding={hasCompletedOnboarding}
            onClickDimensionTitle={() => {
              if (!hasCompletedOnboarding) return
              sendDescriptionToAssistant(
                data?.riasec_elevation_section?.data?.description
              )
            }}
          />
        </Page>
      )}

      {/** ___ */}
      {/** attachement */}

      {/** SKILLS */}
      {attachment_visible && hasSkillsDimensions && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={data?.skills_section?.title}
            description={data?.skills_section?.description}
          />
          <View style={{ marginTop: 10 }} />
          {(data?.skills_section?.data || []).map((dimension, index) => (
            <DimensionCardAppendix
              key={index}
              title={dimension?.title}
              description={dimension?.description}
              endPoleLeftTitle={dimension?.endpoint_left}
              endPoleRightTitle={dimension?.endpoint_right}
              insightDescription={dimension?.insight}
              expertInputValues={dimension?.inputValues}
              maxGreen={dimension?.max_green}
              maxYellow={dimension?.max_yellow}
              minGreen={dimension?.min_green}
              minYellow={dimension?.min_yellow}
              hasCompletedOnboarding={hasCompletedOnboarding}
              containerStyle={{ marginTop: 16 }}
            />
          ))}
        </Page>
      )}
      {/** PERSÖNLICHKEIT */}
      {attachment_visible &&
        hasPersonalityDimensions &&
        !withoutAttachments && (
          <Page {...pageProps}>
            <TitleDescription
              title={data?.personality_section?.title}
              description={data?.personality_section?.description}
            />
            <View style={{ marginTop: 10 }} />
            {(data?.personality_section?.data || []).map((dimension, index) => (
              <DimensionCardAppendix
                key={index}
                title={dimension?.title}
                description={dimension?.description}
                endPoleLeftTitle={dimension?.endpoint_left}
                endPoleRightTitle={dimension?.endpoint_right}
                insightDescription={dimension?.insight}
                expertInputValues={dimension?.inputValues}
                maxGreen={dimension?.max_green}
                maxYellow={dimension?.max_yellow}
                minGreen={dimension?.min_green}
                minYellow={dimension?.min_yellow}
                hasCompletedOnboarding={hasCompletedOnboarding}
                containerStyle={{ marginTop: 16 }}
              />
            ))}
          </Page>
        )}
      {/** CULTURE */}
      {attachment_visible && hasCulture && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={data?.culture_section?.title}
            description={data?.culture_section?.description}
          />
          {isCultureLongMode ? (
            <CultureRankedList
              containerStyle={{ marginHorizontal: 24, marginTop: 26 }}
              data={data?.culture_section?.ranked_dimensions}
              topDimensions={data?.culture_section?.top_dimensions}
            />
          ) : (
            <View
              style={{
                marginHorizontal: 24,
                marginTop: 26
              }}
            >
              <View
                style={{
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.lightGrey,
                  padding: 24,
                  alignItems: 'center'
                }}
              >
                <MarkdownText style={typoStyles.H4Black}>
                  {data?.culture_section?.score_title}
                </MarkdownText>
              </View>
              <View
                style={{
                  borderLeftWidth: 1,
                  borderRightWidth: 1,
                  borderColor: colors.lightGrey
                }}
              >
                <CoordinateSystem
                  companyCultureScores={{
                    HEROS_JOURNEY_score_coopcomp:
                      data?.culture_section?.talent_scores?.score_coopcomp,
                    HEROS_JOURNEY_score_orderchaos:
                      data?.culture_section?.talent_scores?.score_orderchaos
                  }}
                  withCompanyIcon
                />
              </View>

              <View
                style={{
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  backgroundColor: colors.lightPaleGrey,
                  borderWidth: 1,
                  borderColor: colors.lightGrey,
                  padding: 24
                }}
              >
                {(data?.culture_section?.data || []).map((c, index) => (
                  <MarkdownText
                    key={index}
                    style={[
                      typoStyles.T1Black,
                      { marginTop: index === 0 ? 0 : 16, display: 'block' }
                    ]}
                  >
                    {c}
                  </MarkdownText>
                ))}
              </View>
            </View>
          )}
        </Page>
      )}
      {/** INTERESSEN */}
      {attachment_visible && hasInterestsDimensions && !withoutAttachments && (
        <Page {...pageProps}>
          <TitleDescription
            title={data?.interests_section?.title}
            description={data?.interests_section?.description}
          />
          <TopInterests
            title={data?.interests_section?.sub_title}
            data={data?.interests_section?.data?.slice(0, 3)}
            containerStyle={{ marginHorizontal: 24, marginTop: 26 }}
            insight={data?.interests_section?.insight}
            withDimensionDescription
          />

          <View style={{ marginTop: 16 }}>
            <DimensionCardAppendix
              title={data?.riasec_elevation_section?.data?.title}
              description={data?.riasec_elevation_section?.data?.description}
              endPoleLeftTitle={data?.riasec_elevation_section?.endpole_left}
              endPoleRightTitle={data?.riasec_elevation_section?.endpole_right}
              insightDescription={data?.riasec_elevation_section?.insight}
              expertInputValues={
                data?.riasec_elevation_section?.data?.inputValues
              }
              maxGreen={data?.riasec_elevation_section?.data?.max_green}
              maxYellow={data?.riasec_elevation_section?.data?.max_yellow}
              minGreen={data?.riasec_elevation_section?.data?.min_green}
              minYellow={data?.riasec_elevation_section?.data?.min_yellow}
              hasCompletedOnboarding={hasCompletedOnboarding}
              containerStyle={{ marginTop: 16 }}
            />
          </View>
        </Page>
      )}
    </Document>
  )
}

export default RequirementProfile
